"use client";

import { useEffect, useRef } from "react";
import { useTraceStore } from "@/store/useTraceStore";

/**
 * Loads submarine cable GeoJSON data on mount.
 *
 * Populates the Zustand `cables` state and builds a name-to-featureId
 * lookup map that the animation sequencer uses to highlight cables.
 *
 * @returns Object containing refs for the cable name map and loaded flag.
 */
export function useCableLoader() {
  const setCables = useTraceStore((s) => s.setCables);

  const cableNameMapRef = useRef<Map<string, string[]>>(new Map());
  const cablesLoadedRef = useRef(false);

  useEffect(() => {
    fetch("/data/cable-geo.json")
      .then((r) => {
        if (!r.ok) throw new Error(`Cable fetch failed: ${r.status}`);
        return r.json();
      })
      .then((geoJson) => {
        // Validate the GeoJSON structure before processing 728KB of data
        if (!geoJson || !Array.isArray(geoJson.features)) {
          throw new Error(
            `Invalid cable GeoJSON: expected { features: [...] }, got ${typeof geoJson}`
          );
        }
        const nameMap = new Map<string, string[]>();
        let skipped = 0;
        const paths = geoJson.features.flatMap(
          (f: {
            properties?: { name?: string; color?: string; feature_id?: string };
            geometry?: { coordinates?: number[][][] };
          }) => {
            // Guard: skip any feature that is missing required properties or geometry.
            // Malformed entries in the 728KB GeoJSON would otherwise throw inside
            // .map() and discard the entire cable dataset.
            const name = f?.properties?.name;
            const featureId = f?.properties?.feature_id;
            const coords = f?.geometry?.coordinates?.[0];
            if (!name || !featureId || !Array.isArray(coords)) {
              skipped++;
              return [];
            }
            const key = name.toLowerCase();
            if (!nameMap.has(key)) nameMap.set(key, []);
            nameMap.get(key)!.push(featureId);
            return [{
              coords,
              name,
              color: f.properties?.color || "#1a2a3f",
              featureId,
            }];
          }
        );
        if (skipped > 0) {
          console.warn(`useCableLoader: skipped ${skipped} malformed GeoJSON feature(s)`);
        }
        cableNameMapRef.current = nameMap;
        cablesLoadedRef.current = true;
        setCables(paths);
      })
      .catch((err) => {
        console.error("Failed to load submarine cable data:", err);
      });
  }, [setCables]);

  return { cableNameMapRef, cablesLoadedRef };
}
